// BOM data for the screens: one index over the synced BOM tables (shared/boms.ts does the resolving),
// rebuilt only when one of those tables changes and shared by every component that asks.
import { type BomData, type BomIndex, countedStock, currentRevision, indexBoms, type Range } from "../../../shared/boms.ts";
import { calc } from "../../../shared/calc.ts";
import { planToday } from "../../../shared/day.ts";
import type { Item } from "../../../shared/db.gen.ts";
import { money, qty } from "./format.ts";
import { mutate } from "./mutate.ts";
import { navigate, usePath } from "./router.tsx";
import { store, useRows } from "./sync.ts";

let last: { inputs: unknown[]; index: BomIndex } | null = null;

export function useBomIndex(): BomIndex {
  const items = useRows("item");
  const boms = useRows("bom");
  const lines = useRows("bom_line");
  const maps = useRows("bom_option_map");
  const overrides = useRows("bom_override");
  const vendorItems = useRows("vendor_item");
  const products = useRows("shopify_product");
  // Stock to build with (onHandFor in shared/boms.ts): Shopify items' available (on hand less units on open orders,
  // which are sold already) plus local items' ledger balances (an item has one or the other).
  const stockLevels = useRows("stock_level");
  const balances = useRows("stock_balance");
  const locations = useRows("location");
  const charges = useRows("build_charge"); // Charges ---
  const inputs = [items, boms, lines, maps, overrides, vendorItems, products, stockLevels, balances, locations, charges];
  const today = planToday();
  if (!last || last.index.today !== today || last.inputs.some((x, i) => x !== inputs[i])) {
    const ledger = balances.map((b) => ({ item_id: b.item_id, location_id: b.location_id, on_hand: b.qty }));
    const levels = stockLevels.map((s) => ({ item_id: s.item_id, location_id: s.location_id, on_hand: s.available }));
    const stock = countedStock([...levels, ...ledger], locations);
    last = { inputs, index: indexBoms({ items, boms, lines, maps, overrides, vendorItems, products, stock, today, charges } as BomData) };
  }
  return last.index;
}

let lastPriced: { inputs: unknown[]; index: BomIndex } | null = null;

/**
 * Costs --- the same index with prices but no stock (the cost model never reads on-hand), so a stock change (every
 * inventory webhook) keeps it, and the cost index and run results cached on it.
 */
export function useBomPrices(): BomIndex {
  const inputs = [
    useRows("item"),
    useRows("bom"),
    useRows("bom_line"),
    useRows("bom_option_map"),
    useRows("bom_override"),
    useRows("vendor_item"),
    useRows("shopify_product"),
    useRows("build_charge"), // Charges ---
  ];
  const today = planToday();
  if (!lastPriced || lastPriced.index.today !== today || lastPriced.inputs.some((x, i) => x !== inputs[i])) {
    const [items, boms, lines, maps, overrides, vendorItems, products, charges] = inputs;
    lastPriced = { inputs, index: indexBoms({ items, boms, lines, maps, overrides, vendorItems, products, today, charges } as unknown as BomData) };
  }
  return lastPriced.index;
}

let lastStructure: { inputs: unknown[]; index: BomIndex } | null = null;

/**
 * Make planning --- the same index without stock or prices, for the plan (it never reads on-hand or costs through
 * it), so stock and vendor-price changes don't rebuild it, or the BOM graph cached on it.
 */
export function useBomStructure(): BomIndex {
  return bomStructureFor([useRows("item"), useRows("bom"), useRows("bom_line"), useRows("bom_option_map"), useRows("bom_override")]);
}

/** Check against the plan --- the same outside React (an idle job), sharing the cache. */
export const bomStructureNow = () => bomStructureFor(["item", "bom", "bom_line", "bom_option_map", "bom_override"].map((t) => store.rows(t)));

function bomStructureFor(inputs: unknown[]): BomIndex {
  const today = planToday();
  if (!lastStructure || lastStructure.index.today !== today || lastStructure.inputs.some((x, i) => x !== inputs[i])) {
    const [items, boms, lines, maps, overrides] = inputs;
    lastStructure = { inputs, index: indexBoms({ items, boms, lines, maps, overrides, vendorItems: [], today } as unknown as BomData) };
  }
  return lastStructure.index;
}

/** The family an item has a BOM in: its Shopify product's, or its own (sub-assemblies). */
export const familyOfItem = (item: Pick<Item, "kind" | "source" | "id" | "shopify_product_id">) =>
  item.source === "shopify" && item.shopify_product_id ? `product:${item.shopify_product_id}` : item.kind === "subassembly" ? `item:${item.id}` : null;

export const bomPath = (bomId: string, variantId?: string) => `/boms?open=${bomId}${variantId ? `&variant=${variantId}` : ""}`;

/**
 * Where the BOM editor opens (Mitch, 2026-11-02: "Opening a bom closes the project view. it's very irritating"): on the
 * BOMs page, or, while we're in a project, inside its Build tab (`?tab=build&bom=<id>`, with its own `btab` beside the
 * project's `tab`), so a BOM opened from a project (its tabs, a gap, the part panel beside it) keeps the project.
 */
export type BomPlace = {
  /** The address-bar names for the BOM open and the editor's tab. */
  open: string;
  tab: string;
  back: { to: string; label: string };
  path: (bomId: string, variantId?: string) => string;
};
const BOMS_PAGE: BomPlace = { open: "open", tab: "tab", back: { to: "/boms", label: "All BOMs" }, path: bomPath };
export function bomPlaceFor(pathname: string): BomPlace {
  const m = /^\/projects\/([^/?#]+)/.exec(pathname);
  if (!m) return BOMS_PAGE;
  const base = `/projects/${m[1]}`;
  return {
    open: "bom",
    tab: "btab",
    back: { to: `${base}?tab=build`, label: "Back to Build" },
    path: (bomId, variantId) => `${base}?tab=build&bom=${bomId}${variantId ? `&variant=${variantId}` : ""}`,
  };
}
export const useBomPlace = () => bomPlaceFor(usePath());
/** A link to a BOM from wherever we are (in a project: its Build tab; elsewhere the BOMs page). */
export const bomLink = (bomId: string, variantId?: string) => bomPlaceFor(location.pathname).path(bomId, variantId);

/** Starts a family's first draft (or its next revision number) and opens it; a sub-assembly's in the projects given. */
export async function createBom(ix: BomIndex, owner: { shopify_product_id: string } | { item_id: string; project_ids?: string[] }) {
  const key = "shopify_product_id" in owner ? `product:${owner.shopify_product_id}` : `item:${owner.item_id}`;
  const revision = (ix.families.get(key)?.[0]?.revision ?? 0) + 1;
  const id = crypto.randomUUID();
  const ok = await mutate("bom.create", { id, revision, ...owner });
  if (ok) navigate(bomLink(id));
  return ok;
}

export const currentBomOf = (ix: BomIndex, key: string | null) => (key ? currentRevision(ix, key) : undefined);

/** $31.20 for totals (cents are enough). */
export const cost2 = (n: number | null | undefined) => (n == null ? "" : money(Math.round(n * 100) / 100));
export const costRange = (r: Range) => (!r ? "" : Math.abs(r.max - r.min) < 0.005 ? cost2(r.min) : `${cost2(r.min)}–${cost2(r.max)}`);
export const pct = (n: number) => `${Math.round(n * 100)}%`;
export const pctRange = (r: Range) => (!r ? "" : Math.round(r.min * 100) === Math.round(r.max * 100) ? pct(r.min) : `${pct(r.min)}–${pct(r.max)}`);
/** Badge tones for revisionState(). */
export const STATE_TONE = { draft: "accent", active: "ok", scheduled: "accent", superseded: "neutral", retired: "neutral" } as const;

/** "120" or "120–400" units. */
export const unitsRange = (r: Range) => (!r ? "" : r.min === r.max ? qty(r.min) : `${qty(r.min)}–${qty(r.max)}`);

export const marginTone = (m: number | null | undefined) => (m == null ? "text-faint" : m < 0.3 ? "text-danger" : m < 0.5 ? "text-warn" : "text-ok");

/** Parses a quantity typed in a cell ("2", "0.5", "1,000", or a sum: "12*24"); undefined when it isn't a number. */
export function parseQty(text: string): number | undefined {
  const n = calc(text);
  return n != null && n >= 0 ? n : undefined;
}

/** A part by SKU (any case), skipping archived ones. */
export function itemBySku(sku: string) {
  const want = sku.trim().toLowerCase();
  return (store.rows("item") as unknown as Item[]).find((i) => !i.archived && i.sku.toLowerCase() === want);
}
